import type { ReactNode } from 'react';
import { AlertTriangle, Compass } from 'lucide-react';
import { Link, useRouteError } from 'react-router';
import { Dica } from '@/componentes/Dica';
import { Button } from '@/componentes/ui/button';
import { TooltipProvider } from '@/componentes/ui/tooltip';

/** Moldura comum das duas telas: centralizada, com ícone, título, explicação e uma saída. */
function Moldura({
  icone,
  titulo,
  descricao,
  children,
}: {
  icone: ReactNode;
  titulo: string;
  descricao: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto flex min-h-[60dvh] w-full max-w-lg flex-col items-center justify-center gap-4 px-4 text-center">
      <span className="flex size-12 items-center justify-center rounded-full bg-primary/15">
        {icone}
      </span>
      <div className="space-y-1">
        <h1 className="text-2xl">{titulo}</h1>
        <p className="text-sm text-muted-foreground">{descricao}</p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">{children}</div>
    </div>
  );
}

/**
 * `errorElement` da raiz do roteador: um erro de render em qualquer tela cai aqui, com um caminho de
 * volta, em vez de deixar a tela branca.
 *
 * O erro em si vai só para o console: a pessoa não tem o que fazer com um stack, e mostrá-lo na tela
 * exporia nomes de módulo e trechos de código a quem estiver olhando.
 */
export function PaginaErro() {
  const erro = useRouteError();
  console.error('Erro não tratado na interface:', erro);

  // `TooltipProvider` próprio: é a tela de último recurso e não pode depender de contexto de fora para abrir.
  return (
    <TooltipProvider delayDuration={200}>
      <Moldura
        icone={<AlertTriangle className="size-6 text-primary" aria-hidden />}
        titulo="Algo deu errado nesta tela"
        descricao="A página não conseguiu carregar. Recarregar costuma resolver; se insistir, avise a equipe."
      >
        <Dica texto="Carrega a tela de novo do servidor">
          <Button onClick={() => window.location.reload()}>Recarregar a página</Button>
        </Dica>
        {/* `<a>` e não `<Link>`: o roteador acabou de falhar, então a navegação interna pode falhar também. */}
        <Dica texto="Volta para a página inicial do painel">
          <Button variant="outline" asChild>
            <a href="/">Ir para o início</a>
          </Button>
        </Dica>
      </Moldura>
    </TooltipProvider>
  );
}

/** Endereço que não existe. Fica **dentro** da `Shell`, com menu — sair daqui é um clique. */
export function PaginaNaoEncontrada() {
  return (
    <Moldura
      icone={<Compass className="size-6 text-primary" aria-hidden />}
      titulo="Página não encontrada"
      descricao="Este endereço não existe no painel. Pode ter sido um link antigo ou um erro de digitação."
    >
      <Dica texto="Volta para a página inicial do painel">
        <Button asChild>
          <Link to="/">Ir para o início</Link>
        </Button>
      </Dica>
    </Moldura>
  );
}
